<template>
  <div class="kiosk-root" :class="state">
    <!-- ===== IDLE STATE ===== -->
    <transition name="fade">
      <div v-if="state === 'idle'" class="kiosk-idle">
        <div class="idle-icon-ring">
          <feather-icon
            icon="CreditCardIcon"
            svgClasses="w-20 h-20 text-white opacity-80"
          />
        </div>
        <h1 class="idle-title">ยินดีต้อนรับ</h1>
        <p class="idle-subtitle">กรุณาติดต่อเจ้าหน้าที่เพื่อชำระเงิน</p>
        <div class="idle-dots"><span /><span /><span /></div>
      </div>
    </transition>

    <!-- ===== MEMBER STATE ===== -->
    <transition name="slide-up">
      <div v-if="state === 'member'" class="kiosk-member">
        <!-- Header -->
        <div class="kiosk-header">
          <feather-icon
            icon="UserIcon"
            svgClasses="w-7 h-7 mr-3 text-white opacity-80"
          />
          <div>
            <p
              class="text-white text-xs opacity-70 mb-0"
              style="letter-spacing: 1px"
            >
              ยินดีต้อนรับ
            </p>
            <p class="text-white text-2xl font-bold leading-tight">
              {{ memberName }}
            </p>
          </div>
          <div class="ml-auto text-right">
            <p class="text-white text-xs opacity-60">เลขที่ผู้ใช้น้ำ</p>
            <p class="text-white text-lg font-semibold opacity-90">
              {{ memberNo }}
            </p>
          </div>
        </div>

        <!-- Body -->
        <div class="member-body">
          <!-- Member info card -->
          <div class="member-info-card">
            <div class="member-info-row">
              <feather-icon
                icon="MapPinIcon"
                svgClasses="w-5 h-5 text-white opacity-70 mr-3 flex-shrink-0"
              />
              <p class="text-white text-lg opacity-90">{{ address || "-" }}</p>
            </div>
          </div>

          <!-- Bills section -->
          <div class="member-bills-section">
            <div class="bills-header">
              <feather-icon
                icon="FileTextIcon"
                svgClasses="w-6 h-6 mr-2"
                style="color: #ffd54f"
              />
              <span class="text-white text-xl font-bold">รายการค้างชำระ</span>
              <span class="bills-count-badge"
                >{{ allBills.length }} รายการ</span
              >
            </div>

            <div v-if="allBills.length === 0" class="no-bills-box">
              <feather-icon
                icon="CheckCircleIcon"
                svgClasses="w-16 h-16 mb-4"
                style="color: #69f0ae"
              />
              <p class="text-white text-2xl font-bold mb-2">
                ไม่มีรายการค้างชำระ
              </p>
              <p class="text-white opacity-70 text-lg">ชำระเงินครบถ้วนแล้ว</p>
            </div>

            <div v-else class="bills-grid">
              <div v-for="(bill, i) in allBills" :key="i" class="bill-card">
                <div class="bill-card-period">
                  <feather-icon
                    icon="CalendarIcon"
                    svgClasses="w-4 h-4 mr-1 opacity-70"
                  />
                  {{ bill.period }}
                </div>
                <div class="bill-card-no">{{ bill.billNo }}</div>
                <div class="bill-card-amount">
                  {{ fmtAmount(bill.total) }}
                  <span class="bill-card-unit">บาท</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer instruction -->
        <div class="kiosk-footer member-footer">
          <feather-icon
            icon="MessageCircleIcon"
            svgClasses="w-5 h-5 mr-2"
            style="color: #ffd54f"
          />
          <span class="text-white text-base"
            >กรุณาแจ้งเจ้าหน้าที่ว่าต้องการชำระรายการใด</span
          >
        </div>
      </div>
    </transition>

    <!-- ===== QR STATE ===== -->
    <transition name="slide-up">
      <div v-if="state === 'qr'" class="kiosk-qr">
        <!-- Header -->
        <div class="kiosk-header">
          <feather-icon
            icon="UserIcon"
            svgClasses="w-6 h-6 mr-2 text-white opacity-80"
          />
          <span class="text-white text-xl font-semibold">{{ memberName }}</span>
          <span v-if="memberNo" class="ml-auto text-white opacity-60 text-base"
            >เลขที่: {{ memberNo }}</span
          >
        </div>

        <!-- Main content -->
        <div class="kiosk-body">
          <!-- QR Code -->
          <div class="qr-box">
            <div class="qr-frame">
              <promptpay-qr
                v-if="promptpayAccount"
                :id="promptpayAccount"
                :amount="amount"
                :size="qrSize"
              />
            </div>
            <p class="scan-hint">
              <feather-icon
                icon="SmartphoneIcon"
                svgClasses="w-5 h-5 mr-2 inline-block"
              />
              สแกนด้วยแอปธนาคาร
            </p>
          </div>

          <!-- Amount -->
          <div class="amount-box">
            <p class="amount-label">ยอดชำระ</p>
            <p class="amount-value">{{ fmtAmount(amount) }}</p>
            <p class="amount-unit">บาท</p>

            <div class="bill-chips">
              <div v-for="(bill, i) in bills" :key="i" class="bill-chip">
                <feather-icon icon="FileTextIcon" svgClasses="w-4 h-4 mr-1" />
                {{ bill.period }} — {{ fmtAmount(bill.total) }} บาท
              </div>
            </div>

            <div class="expire-bar" v-if="countdown > 0">
              <feather-icon icon="ClockIcon" svgClasses="w-4 h-4 mr-1 text-white" />
              <p class="text-white text-sm">QR หมดอายุใน {{ countdown }} วินาที</p>
              <div class="expire-progress-track">
                <div
                  class="expire-progress-fill"
                  :style="{ width: expirePercent + '%' }"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="kiosk-footer">
          <feather-icon
            icon="ShieldIcon"
            svgClasses="w-4 h-4 mr-1 opacity-60"
          />
          <span class="opacity-60 text-sm"
            >ระบบชำระเงินปลอดภัย · PromptPay</span
          >
        </div>
      </div>
    </transition>

    <!-- ===== PAID STATE ===== -->
    <transition name="fade">
      <div v-if="state === 'paid'" class="kiosk-paid">
        <div class="paid-icon-ring">
          <feather-icon
            icon="CheckCircleIcon"
            svgClasses="w-24 h-24 text-white"
          />
        </div>
        <h1 class="paid-title">ชำระเงินสำเร็จ</h1>
        <p class="paid-amount">{{ fmtAmount(amount) }} บาท</p>
        <p class="paid-subtitle">ขอบคุณที่ใช้บริการ</p>
      </div>
    </transition>
  </div>
</template>

<script>
import PromptpayQr from "vue-promptpay-qr";

const CHANNEL_NAME = "prapa_payment_qr";
const QR_TTL_SECONDS = 180; // 3 minutes

export default {
  name: "PaymentQRDisplay",
  components: { PromptpayQr },

  data() {
    return {
      state: "idle", // 'idle' | 'member' | 'qr' | 'paid'
      promptpayAccount: "",
      memberName: "",
      memberNo: "",
      address: "",
      amount: 0,
      bills: [], // selected bills (qr state)
      allBills: [], // all outstanding bills (member state)

      qrSize: 280,
      countdown: 0,
      expirePercent: 100,
      channel: null,
      countdownTimer: null,
    };
  },

  mounted() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e) => this.handleMessage(e.data);

    // Announce ready to officer tab
    this.channel.postMessage({ type: "KIOSK_READY" });

    // Handle window resize
    this.updateQrSize();
    window.addEventListener("resize", this.updateQrSize);
  },

  beforeDestroy() {
    if (this.channel) this.channel.close();
    this.clearTimer();
    window.removeEventListener("resize", this.updateQrSize);
  },

  methods: {
    handleMessage(data) {
      switch (data.type) {
        case "SHOW_MEMBER":
          this.showMember(data);
          break;
        case "SHOW_QR":
          this.showQR(data);
          break;
        case "PAID":
          this.showPaid();
          break;
        case "CLEAR":
          this.goIdle();
          break;
      }
    },

    showMember(data) {
      this.clearTimer();
      this.memberName = data.memberName || "";
      this.memberNo = data.memberNo || "";
      this.address = data.address || "";
      this.allBills = data.bills || [];
      this.state = "member";
    },

    showQR(data) {
      this.memberName = data.memberName || "";
      this.memberNo = data.memberNo || "";
      this.amount = data.amount || 0;
      this.bills = data.bills || [];
      this.promptpayAccount = data.promptpayAccount || "";
      this.state = "qr";
      this.startCountdown(QR_TTL_SECONDS);
    },

    showPaid() {
      this.clearTimer();
      this.state = "paid";
      setTimeout(() => this.goIdle(), 5000);
    },

    goIdle() {
      this.clearTimer();
      this.state = "idle";
      this.memberName = "";
      this.memberNo = "";
      this.address = "";
      this.amount = 0;
      this.bills = [];
      this.allBills = [];
    },

    startCountdown(seconds) {
      this.clearTimer();
      this.countdown = seconds;
      this.expirePercent = 100;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        this.expirePercent = (this.countdown / QR_TTL_SECONDS) * 100;
        if (this.countdown <= 0) {
          this.clearTimer();
          this.goIdle();
        }
      }, 1000);
    },

    clearTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    updateQrSize() {
      const w = window.innerWidth;
      this.qrSize = w < 600 ? 220 : w < 900 ? 260 : 300;
    },

    fmtAmount(val) {
      return parseFloat(val || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style lang="scss">
body {
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
// ---- Root ----
.kiosk-root {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.6s ease;
  overflow: hidden;
  position: relative;

  &.idle {
    background: linear-gradient(145deg, #0f2027, #203a43, #2c5364);
  }
  &.member {
    background: linear-gradient(145deg, #1a0533, #311b6b, #1565c0);
  }
  &.qr {
    background: linear-gradient(145deg, #1a237e, #1565c0, #0288d1);
  }
  &.paid {
    background: linear-gradient(145deg, #1b5e20, #2e7d32, #388e3c);
  }
}

// ---- Idle ----
.kiosk-idle {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.idle-icon-ring {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  animation: pulse 2.5s infinite ease-in-out;
  margin-bottom: 32px;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.15);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 24px rgba(255, 255, 255, 0);
    transform: scale(1.04);
  }
}

.idle-title {
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 12px;
  letter-spacing: -1px;
}

.idle-subtitle {
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 40px;
}

.idle-dots {
  display: flex;
  gap: 10px;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    animation: dot-bounce 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.7);
    opacity: 0.4;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

// ---- Shared header / footer ----
.kiosk-header {
  display: flex;
  align-items: center;
  padding: 20px 40px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  flex-shrink: 0;
}

.kiosk-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.member-footer {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(0, 0, 0, 0.3);
  padding: 18px;
  font-size: 1.05rem;
}

// ---- Member State ----
.kiosk-member {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.member-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px 40px;
  gap: 20px;
  overflow: hidden;
}

.member-info-card {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 16px 24px;
  flex-shrink: 0;
}

.member-info-row {
  display: flex;
  align-items: center;
}

.member-bills-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.bills-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.bills-count-badge {
  margin-left: 12px;
  background: rgba(255, 213, 79, 0.25);
  border: 1px solid rgba(255, 213, 79, 0.4);
  color: #ffd54f;
  border-radius: 20px;
  padding: 2px 14px;
  font-size: 0.9rem;
  font-weight: 600;
}

.no-bills-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.bills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  overflow-y: auto;
  flex: 1;
  padding-right: 4px;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
}

.bill-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  padding: 16px 20px;
  backdrop-filter: blur(4px);
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
}

.bill-card-period {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.bill-card-no {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  margin-bottom: 8px;
}

.bill-card-amount {
  color: #ffd54f;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
}

.bill-card-unit {
  font-size: 0.9rem;
  font-weight: 400;
  opacity: 0.8;
}

// ---- QR Layout ----
.kiosk-qr {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.kiosk-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 60px;
  padding: 20px 40px;
  flex-wrap: wrap;
}

// ---- QR Box ----
.qr-box {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-frame {
  padding: 18px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 6px rgba(255, 255, 255, 0.15);
  animation: qr-appear 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);

  canvas {
    display: block;
    border-radius: 6px;
  }
}

@keyframes qr-appear {
  from {
    transform: scale(0.7);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.scan-hint {
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.1rem;
  display: flex;
  align-items: center;
}

// ---- Amount Box ----
.amount-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 260px;
}

.amount-label {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.65);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 4px;
}

.amount-value {
  font-size: 4rem;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  letter-spacing: -2px;
}

.amount-unit {
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 24px;
}

.bill-chips {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
}

.bill-chip {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  padding: 6px 16px;
  color: #ffffff;
  font-size: 0.9rem;
  backdrop-filter: blur(4px);
}

// ---- Countdown ----
.expire-bar {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 300px;
}

.expire-progress-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.expire-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ffeb3b, #ff9800);
  border-radius: 2px;
  transition: width 1s linear;
}

// ---- Paid ----
.kiosk-paid {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.paid-icon-ring {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  animation: paid-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  margin-bottom: 28px;
}

@keyframes paid-pop {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.paid-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 8px;
}

.paid-amount {
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 12px;
}

.paid-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.6);
}

// ---- Transitions ----
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter {
  transform: translateY(40px);
  opacity: 0;
}
.slide-up-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
